import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'hooks';

import { GenericError, Loader } from 'components';
import DateUtils from 'utils/dateUtils';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { GetTeamProfileApiResponse } from 'containers/team-profile/teamProfile.model';
import { formatAmount } from 'utils/amountUtils';
import { selectEstimate } from '../estimatesSlice';

// Import shared components that can be reused from invoices
import BillToSection from '../../invoices/components/BillToSection';
import NotesSection from '../../invoices/components/NotesSection';
import AttachmentsSection from '../../invoices/components/AttachmentsSection';
import TotalSection from '../../invoices/components/TotalSection';
import TeamDetailsSection from '../../invoices/components/TeamDetailsSection';
import InvoiceLineItems from 'containers/invoices/components/InvoiceLineItems';
import { generateCreateEstimatePayload } from '../utils';
import { useGetEstimatePDFPreviewMutation } from '../api';
import TermsSection from 'containers/invoices/components/TermsSection';
import InvoiceHeader from 'containers/invoices/components/InvoiceHeader';
import { LINE_ITEM_SOURCE } from 'constants/common';

enum PageState {
  BUYER_VIEW = 'BUYER_VIEW',
  PDF_VIEW = 'PDF_VIEW'
}

interface EstimateBuyersPreviewProps {
  goBack: () => void;
  getTeamProfileData: GetTeamProfileApiResponse;
  onCreateAndShare: () => void;
  isCreating: boolean;
}

const EstimateBuyersPreview: FC<EstimateBuyersPreviewProps> = ({
  goBack,
  getTeamProfileData,
  onCreateAndShare,
  isCreating
}) => {
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');

  const [pageState, setPageState] = useState<PageState>(PageState.BUYER_VIEW);

  const [getEstimatePDFPreview, getEstimatePDFPreviewQueryState] = useGetEstimatePDFPreviewMutation();
  const {
    data: previewUrl,
    isLoading: isPDFPreviewFetching,
    isError: isPDFPreviewError
  } = getEstimatePDFPreviewQueryState;

  const selectedEstimate = useAppSelector(selectEstimate);
  const {
    estimateCustomer,
    estimateLineItems,
    estimateSubTotal,
    estimateFinalAmount,
    attachments,
    notes,
    estimateSelectedServiceCharge,
    estimateAppliedLineItemLevelTaxes,
    estimateDiscountFormData,
    discountSettings,
    expiryDate,
    estimateIsServiceFeeEnabled,
    estimateCurrentSurcharge,
    estimatesTnc,
    estimateTitle,
    estimateCreatedAt,
    estimateUpdatedAt,
    estimateNumber
  } = selectedEstimate;
  const currentTeam = useAppSelector(selectCurrentTeam);

  const onPDFPreviewClick = () => {
    if (!previewUrl) {
      const estimatePayloadPreview = generateCreateEstimatePayload();
      getEstimatePDFPreview({ teamId: currentTeam.id, payload: { ...estimatePayloadPreview, estimateNumber } });
    }
    setPageState(PageState.PDF_VIEW);
  };

  return (
    <div className="flex h-full w-full flex-col border">
      <InvoiceHeader
        title={`${isEdit ? 'Save' : 'Create'} & Share`}
        pageState={pageState}
        setPageState={setPageState}
        onPDFPreviewClick={onPDFPreviewClick}
        editAndShareHandler={onCreateAndShare}
        goBack={goBack}
        isActionLoading={isCreating}
      />

      {pageState === PageState.BUYER_VIEW && (
        <div className="flex grow gap-8 bg-borderGray px-[149px] pt-8 pb-[102px]">
          <div className="flex w-[675px] flex-col gap-[30px]">
            <BillToSection customer={{ ...estimateCustomer, id: estimateCustomer.ID }} isEstimate={true} />
            <InvoiceLineItems
              items={estimateLineItems}
              billAmount={estimateSubTotal}
              totalAmount={estimateFinalAmount}
              surChargeAmount={+(estimateCurrentSurcharge.valueInCurrencyWithBps ?? 0)}
              serviceFeeEnabled={estimateIsServiceFeeEnabled}
              serviceCharges={estimateSelectedServiceCharge}
              taxes={estimateAppliedLineItemLevelTaxes}
              discountData={estimateDiscountFormData}
              selectDiscountSettingsForInvoice={discountSettings}
              title={estimateTitle}
              type={LINE_ITEM_SOURCE.ESTIMATES}
            />

            {notes && <NotesSection notes={notes} />}

            {estimatesTnc && <TermsSection terms={estimatesTnc} />}

            {attachments && attachments.length > 0 && (
              <AttachmentsSection attachments={attachments.map(item => ({ ...item, description: '' }))} />
            )}
          </div>

          <div className="flex w-[435px] flex-col gap-[30px]">
            <TotalSection
              total={formatAmount(estimateFinalAmount)}
              dueDate={null}
              isEstimate={true}
              date={DateUtils.getDateInFormat({
                date: estimateCreatedAt ? new Date(estimateCreatedAt) : new Date(),
                dateFormat: 'dd MMM yyyy, h:mm a'
              })}
              expiryDate={
                expiryDate ? DateUtils.getDateInFormat({ date: new Date(expiryDate), dateFormat: 'dd MMM yyyy' }) : ''
              }
              updatedAt={DateUtils.getDateInFormat({
                date: estimateUpdatedAt ? new Date(estimateUpdatedAt) : new Date(),
                dateFormat: 'dd MMM yyyy, h:mm a'
              })}
            />
            <TeamDetailsSection
              teamName={currentTeam.name}
              teamPhone={getTeamProfileData.phone}
              teamEmail={getTeamProfileData.email}
              teamBillingAddress={getTeamProfileData.address}
              teamWebsite={getTeamProfileData.website}
            />
          </div>
        </div>
      )}

      {pageState === PageState.PDF_VIEW && (
        <>
          {previewUrl && (
            <div>
              <object
                style={{
                  height: window.innerHeight,
                  width: window.innerWidth
                }}
                data={`${previewUrl}#toolbar=0`}
                type="application/pdf"></object>
            </div>
          )}
          {isPDFPreviewFetching && <Loader />}
          {isPDFPreviewError && <GenericError handleErrorBack={() => setPageState(PageState.BUYER_VIEW)} />}
        </>
      )}
    </div>
  );
};

export default EstimateBuyersPreview;
